import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { FileText, Zap, BookOpen, ShieldCheck, Lock } from 'lucide-react';
export function Hero() {
  useEffect(() => {
    // Inject preconnect and preload links into head for performance
    const links = [{
      rel: 'preconnect',
      href: 'https://fonts.googleapis.com'
    }, {
      rel: 'preconnect',
      href: 'https://fonts.gstatic.com',
      crossOrigin: 'anonymous'
    }, {
      rel: 'preload',
      href: 'https://fonts.gstatic.com/s/inter/v18/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous'
    }];
    const elements = links.map(link => {
      const el = document.createElement('link');
      Object.entries(link).forEach(([key, value]) => el.setAttribute(key, value as string));
      document.head.appendChild(el);
      return el;
    });
    return () => elements.forEach(el => document.head.removeChild(el));
  }, []);
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="bg-navy text-white py-6 px-4 md:px-6 relative overflow-hidden min-h-[80svh] flex flex-col justify-center" data-id="element-170">
      <div className="max-w-4xl mx-auto text-center relative z-10 w-full" data-id="element-171">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} style={{
        willChange: 'transform, opacity'
      }} data-id="element-172">
          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-gold font-semibold text-xs md:text-sm mb-2 border border-gold/20" data-id="element-173">
            <span className="text-base" data-id="element-174">🏆</span> CHERUB AGENCY™
          </div>

          <h1 className="font-display md:text-4xl lg:text-5xl font-bold mb-1 md:mb-2 text-[28px]" data-id="element-175">
            Todo lo que necesitas para ayudar a tu hijo con TDAH.
          </h1>

          <p className="text-gold-light font-display text-lg md:text-2xl font-semibold mb-3 md:mb-4" data-id="element-176">
            Listo. Desde hoy.
          </p>

          {/* Mockup Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} style={{
          willChange: 'transform, opacity'
        }} className="mb-3 md:mb-4" data-id="element-177">
            <img src="https://cdn.magicpatterns.com/uploads/n2Ry5FFzPdaA7PdnWbSwSV/a764c523-3e06-46ff-9653-546e315567a8.png" alt="TDAH: El Sistema Definitivo - 10 Tomos del MegaPack Completo" width={665} height={282} fetchPriority="high" decoding="async" className="w-full max-w-2xl mx-auto rounded-lg shadow-[0_20px_50px_rgba(201,168,76,0.2)] object-contain max-h-[25vh] md:max-h-[35vh]" data-id="element-178" />
          </motion.div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3 md:p-4 mb-3 max-w-3xl mx-auto backdrop-blur-sm" data-id="element-179">
            <h2 className="text-gold md:text-lg font-semibold mb-2 text-[20px]" data-id="element-180">
              El sistema N°1 para padres de niños con TDAH — De la crisis al
              éxito
            </h2>
            <p className="text-gray-300 md:text-base text-[14px]" data-id="element-181">
              Otros padres improvisan. Tú no. Rutinas que funcionan, límites sin
              gritos y deberes sin pelea. Todo listo para aplicar hoy.
            </p>
          </div>

          <motion.button whileHover={{
          scale: 1.05,
          filter: 'brightness(1.1)'
        }} whileTap={{
          scale: 0.95
        }} onClick={scrollToPricing} aria-label="Ir a las opciones de compra del MegaPack" className="bg-gold text-navy font-bold text-base md:text-lg px-6 py-3 rounded-xl shadow-[0_0_30px_rgba(201,168,76,0.3)] hover:shadow-[0_0_40px_rgba(201,168,76,0.5)] transition-all w-full md:w-auto flex items-center justify-center gap-2 mx-auto mb-3" data-id="element-182">
            <span className="text-xl" data-id="element-183">💪</span> → QUIERO MI MEGAPACK AHORA
          </motion.button>

          <div className="flex items-center justify-center gap-2 mb-4" data-id="element-184">
            <div className="flex gap-0.5" data-id="element-185">
              {[...Array(5)].map((_, i) => <svg key={i} className="w-4 h-4 fill-gold text-gold" viewBox="0 0 20 20" data-id="element-186">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-187" />
                </svg>)}
            </div>
            <span className="text-xs font-bold text-white/80 uppercase tracking-tighter" data-id="element-188">
              +500 Familias satisfechas •{' '}
              <span className="text-gold" data-id="element-189">Verificado</span>
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-sm md:text-base font-medium text-gray-200 mb-3" data-id="element-190">
            <div className="flex items-center gap-1.5 text-[12px]" data-id="element-191">
              <FileText className="w-4 h-4 text-gold" data-id="element-192" /> 100% PDF
            </div>
            <div className="flex items-center gap-1.5 text-[12px]" data-id="element-193">
              <Zap className="w-4 h-4 text-gold" data-id="element-194" /> Acceso en 5 min
            </div>
            <div className="flex items-center gap-1.5 text-[12px]" data-id="element-195">
              <BookOpen className="w-4 h-4 text-gold" data-id="element-196" /> Todos los niveles
            </div>
            <div className="flex items-center gap-1.5 text-[12px]" data-id="element-197">
              <ShieldCheck className="w-4 h-4 text-gold" data-id="element-198" /> Garantía 7 días
            </div>
            <div className="flex items-center gap-1.5 text-[12px]" data-id="element-199">
              <Lock className="w-4 h-4 text-gold" data-id="element-200" /> Pago seguro
            </div>
          </div>
        </motion.div>
      </div>

      {/* Subtle background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-6xl h-full overflow-hidden pointer-events-none opacity-20" data-id="element-201">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-gold blur-[120px]" data-id="element-202"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-gold blur-[120px]" data-id="element-203"></div>
      </div>
    </section>;
}